import dayjs, { Dayjs } from "dayjs";
import { EntryLogTable } from "features/log/components/table/entry-log";
import { VehicleTopBar } from "features/log/components/Top-bar/vehicle-top-bar";
import { useGetLogList } from "features/log/use-cases/get-getSuppliers";
import { omit } from "lodash";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatDate } from "utils/date";

const PAGE_SIZE_KEY = "entry_log_page_size";

/* ---------------------------------- */
/* Helper: string[] → dayjs[]          */
/* ---------------------------------- */
const toDayjsRange = (dates?: string[]): Dayjs[] | undefined => {
  if (!dates || dates.length !== 2) return undefined;

  const start = dayjs(dates[0]);
  const end = dayjs(dates[1]);

  return start.isValid() && end.isValid() ? [start, end] : undefined;
};

export const LogView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [userEditData, setUserEditData] = useState<any>();
  const [showAppUserModal, setShowAppUserModal] = useState(false);

  /* ---------------- URL → params ---------------- */
  const params = useMemo(() => {
    const obj = Object.fromEntries(searchParams.entries());

    return {
      page: Number(obj.page) || 1,
      size:
        Number(obj.size) ||
        Number(localStorage.getItem(PAGE_SIZE_KEY)) ||
        10,

      DriverName: obj.DriverName || undefined,
      SupplierName: obj.SupplierName || undefined,
      VehicleNumber: obj.VehicleNumber || undefined,
      EntryTypeId: obj.EntryTypeId ? Number(obj.EntryTypeId) : undefined,
      StatusId: obj.StatusId ? Number(obj.StatusId) : undefined,

      // 🔥 Convert URL strings → dayjs[]
      date: toDayjsRange(searchParams.getAll("date")),
      date1: toDayjsRange(searchParams.getAll("date1")),
    };
  }, [searchParams]);

  /* ---------------- Prepare API Payload ---------------- */
  const payload = useMemo(() => {
    const entryStart = params.date?.[0];
    const entryEnd = params.date?.[1];
    const exitStart = params.date1?.[0];
    const exitEnd = params.date1?.[1];

    return {
      ...omit(params, ["date", "date1"]),
      EntryStartDate:
        entryStart?.isValid()
          ? formatDate({
              date: entryStart.startOf("day"),
              format: "dbDate",
            })
          : undefined,

      EntryEndDate:
        entryEnd?.isValid()
          ? formatDate({
              date: entryEnd.endOf("day"),
              format: "dbDate",
            })
          : undefined,

      ExitStartDate:
        exitStart?.isValid()
          ? formatDate({
              date: exitStart.startOf("day"),
              format: "dbDate",
            })
          : undefined,

      ExitEndDate:
        exitEnd?.isValid()
          ? formatDate({
              date: exitEnd.endOf("day"),
              format: "dbDate",
            })
          : undefined,
    };
  }, [params]);

  /* ---------------- API ---------------- */
  const getvehcListQueryResponse = useGetLogList(payload, {
    retry: false,
  });

  /* ---------------- Filters ---------------- */
  const handleFiltersChange = (values: any) => {
    const search: Record<string, string | string[]> = {
      page: "1",
      size: params.size.toString(),
    };

    Object.entries(values).forEach(([key, value]:any) => {
      if (!value) return;

      if (key === "date" || key === "date1") {
        search[key] = value.map((d: Dayjs) => d.toISOString());
      } else {
        search[key] = String(value);
      }
    });

    setSearchParams(search);
  };

  /* ---------------- Pagination ---------------- */
  const handleTableChange = (newParams: { page: number; size: number }) => {
    localStorage.setItem(PAGE_SIZE_KEY, newParams.size.toString());

    const currentFilters = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) =>
          key !== "page" &&
          key !== "size" &&
          value !== undefined &&
          value !== ""
      )
    );

    setSearchParams({
      ...currentFilters,
      page: newParams.page.toString(),
      size: newParams.size.toString(),
    });
  };

  /* ---------------- Initial Form Values ---------------- */
  const getInitialFilterValues: any = {
    DriverName: params.DriverName,
    SupplierName: params.SupplierName,
    VehicleNumber: params.VehicleNumber,
    EntryTypeId: params.EntryTypeId,
    StatusId: params.StatusId,
    date: params.date,
    date1: params.date1,
  };

  return (
    <>
      <VehicleTopBar
        getInitialFilterValues={getInitialFilterValues}
        handleFilter={handleFiltersChange}
        userEditData={userEditData}
        showAppUserModal={showAppUserModal}
        setShowAppUserModal={setShowAppUserModal}
        onRefresh={getvehcListQueryResponse.refetch}
      />

      <EntryLogTable
        isvehicleListLoading={getvehcListQueryResponse.isFetching}
        vehicleList={getvehcListQueryResponse?.data?.res?.items}
        itemsCount={getvehcListQueryResponse.data?.totalRecords || 0}
        tableParams={params as any}
        updateParams={handleTableChange as any}
        setUserEditData={setUserEditData}
        setShowAppUserModal={setShowAppUserModal}
        onRefresh={getvehcListQueryResponse.refetch}
        isFetching={getvehcListQueryResponse.isFetching}
      />
    </>
  );
};
